/**
 * Theme system type definitions for WebIMAP
 *
 * Three-layer CSS token architecture:
 * - Primitive tokens: Raw color values
 * - Semantic tokens: Purpose-based mappings
 * - Component tokens: Component-specific tokens
 */

/**
 * Available theme names
 */
export type ThemeName = 'light' | 'dark' | 'auto';

/**
 * Theme definition
 */
export interface Theme {
  /** Unique theme identifier */
  name: ThemeName;

  /** User-facing display name */
  displayName: string;

  /** Whether this is a dark theme */
  isDark: boolean;

  /** User-facing description */
  description: string;
}

/**
 * VueUse useColorMode configuration
 */
export interface ThemeConfig {
  /** HTML attribute to set (default: 'data-theme') */
  attribute: string;

  /** Available theme modes */
  modes: Record<ThemeName, string>;

  /** localStorage key for persistence */
  storageKey: string;
}

/**
 * Available themes in the system
 */
export const themes: Theme[] = [
  {
    name: 'auto',
    displayName: 'Auto (System)',
    isDark: false,
    description: 'Follow system preference (light/dark mode)'
  },
  {
    name: 'light',
    displayName: 'Gmail Classic (Light)',
    isDark: false,
    description: 'Classic Gmail light theme with familiar colors'
  },
  {
    name: 'dark',
    displayName: 'Dark Theme',
    isDark: true,
    description: 'Dark theme for low-light environments'
  }
];

/**
 * Get theme by name
 */
export function getTheme(name: ThemeName): Theme | undefined {
  return themes.find(theme => theme.name === name);
}

/**
 * Default theme configuration for VueUse
 */
export const defaultThemeConfig: ThemeConfig = {
  attribute: 'data-theme',
  modes: {
    light: 'light',
    dark: 'dark',
    auto: 'auto'
  },
  storageKey: 'webimap-theme'
};
